import { omitBy } from 'lodash-es';
import assert from 'minimalistic-assert';
import { decrypt, encrypt, generateKey, randomBytes } from '../crypto.js';
import { retry } from '../utils.js';
class RelayTransport {
    #mpcClient;
    #signerUrl;
    #openSigner;
    #closeSigner;
    #queryParams;
    #listeners = [];
    constructor({ mpcClient, openSigner = (url) => window.open(url, '_blank'), closeSigner = () => { }, config: { signerUrl, queryParams = {} }, }) {
        this.#mpcClient = mpcClient;
        this.#signerUrl = signerUrl;
        this.#openSigner = openSigner;
        this.#closeSigner = closeSigner;
        this.#queryParams = omitBy(queryParams, (value) => value === null || value === undefined);
    }
    async write(data) {
        const conversationId = randomBytes(16).toString('hex');
        const url = new URL(this.#signerUrl);
        const cryptoKey = await generateKey();
        const secret = Buffer.from(await window.crypto.subtle.exportKey('raw', cryptoKey));
        const params = new URLSearchParams({
            ...this.#queryParams,
            conversationId,
            secret: secret.toString('hex'),
        });
        url.hash = params.toString();
        this.#openSigner(url.toString());
        const encrypted = await encrypt(data, cryptoKey);
        const { id: messageId } = await this.#mpcClient.api.messages.create({
            conversationId,
            payload: Buffer.from(encrypted).toString('base64'),
        });
        try {
            const result = await retry(() => this.#mpcClient.api.messages.take({
                conversationId,
                notEqual: messageId,
            }));
            const decrypted = await decrypt(Buffer.from(result.payload, 'base64'), cryptoKey);
            const payload = Buffer.from(decrypted).toString('utf8');
            this.#listeners.forEach((listener) => listener(payload));
        }
        catch (error) {
            const payload = {
                error: error instanceof Error ? error.message : String(error),
            };
            this.#listeners.forEach((listener) => listener(payload));
        }
        this.#closeSigner();
    }
    on(eventName, listener) {
        assert(eventName === 'data', 'only data event is supported');
        this.#listeners.push(listener);
    }
    close() {
        this.#listeners = [];
    }
    removeListener(eventName, listener) {
        assert(eventName === 'data', 'only data event is supported');
        this.#listeners = this.#listeners.filter((it) => it !== listener);
    }
}
export default RelayTransport;
